import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Spinner, EmptyState, Button } from "../components/ui";
import { Icon } from "../components/icons";
import { useAuth } from "../auth";
import { can } from "../lib/permissions";
import { TrackList } from "../components/TrackList";
import { exportSetlistPdf } from "../lib/pdf";

/** Official setlist (not tied to a single event) shown as a playlist; only setlists.edit may reorder. */
export default function OfficialSetlist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const q = useQuery({ queryKey: ["setlist", id], queryFn: () => api(`/setlists/${id}`) });
  const sl = q.data || {};
  const songs = sl.songs || [];
  const canEdit = can(user, "setlists.edit");
  const reorder = useMutation({
    mutationFn: (ids) => api(`/admin/setlists/${id}`, { method: "PUT", body: JSON.stringify({ name: sl.name, description: sl.description || "", song_ids: [...ids, ...(sl.song_ids || []).filter((x) => !ids.includes(x))], member_ids: sl.member_ids || [] }) }),
    onMutate: (ids) => { const byId = Object.fromEntries(songs.map((s) => [s.id, s])); qc.setQueryData(["setlist", id], { ...sl, songs: ids.map((x) => byId[x]) }); },
    onError: (e) => { setErr(e.message); q.refetch(); },
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["admin-setlists"] }); },
  });
  if (q.isLoading) return <Spinner />;
  if (q.isError) return <div className="page"><EmptyState icon="cloud-offline-outline" text="Não foi possível carregar o alinhamento." testid="official-setlist-error" /></div>;
  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/estudar"))}><Icon name="chevron-back" size={22} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-kicker">ALINHAMENTO OFICIAL</div>
          <h1 className="t-h1" style={{ fontSize: 26 }} data-testid="official-setlist-title">{sl.name}</h1>
          {sl.description ? <div className="t-hint">{sl.description}</div> : null}
        </div>
        {songs.length ? <Button className="auto sm ghost" icon="download" onClick={() => exportSetlistPdf({ title: sl.name, songs }, () => "Alinhamento")} data-testid="official-setlist-pdf">PDF</Button> : null}
        {canEdit ? <Button className="auto sm ghost" icon="pencil" onClick={() => navigate(`/admin/setlists?edit=${id}`)} data-testid="official-setlist-edit">EDITAR</Button> : null}
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 820 }}>
        {err ? <div style={{ color: "var(--on-error)", marginBottom: 8 }}>{err}</div> : null}
        <TrackList songs={songs} ctx={{ key: `setlist:${id}`, label: sl.name, path: `/alinhamentos/${id}` }} canReorder={canEdit} onReorder={(ids) => reorder.mutate(ids)} testid="official-track" emptyText="Este alinhamento ainda não tem músicas." />
      </div>
    </>
  );
}
